/**
 * Account handlers for MSW
 * Handles account detail and statistics endpoints
 */

import { faker } from '@faker-js/faker'
import { http, HttpResponse } from 'msw'
import { getMockData } from '../data'
import { generateAccount, calculateAccountStatistics } from '../data/account'
import { applyDelay, handleErrorSimulation } from '../utils'
import type {
  BffAccountResponse,
  BffCredentialsResponse,
  BffCredentialsCreateResponse,
  BffCredentialsDeleteResponse,
  BffCredentialsLinkCreateResponse,
  BffCredentialsLinkDeleteResponse,
  BffCredentialsLinksResponse,
  BffLinkedAccount,
  BffProvider
} from '#shared/types/account'
import type { AccountListItem } from '#shared/types/admin'

// In-memory credentials state for demo mode
interface MockCredentialsState {
  has_credentials: boolean
  client_id: string | null
  client_secret: string | null
  created_at: string | null
}

let mockCredentialsState: MockCredentialsState = {
  has_credentials: false,
  client_id: null,
  client_secret: null,
  created_at: null
}

const PRIMARY_ENTITY_UUID = '550e8400-e29b-41d4-a716-446655440000'

let mockLinkedAccounts: BffLinkedAccount[] = []

// Accounts that already have credentials from a different client (409 simulation)
const accountsWithOtherCredentials = new Set<string>([
  'ee0e8400-e29b-41d4-a716-446655440009'
])

function buildLinksResponse(): BffCredentialsLinksResponse {
  return {
    client_id: mockCredentialsState.client_id!,
    source_entity_uuid: PRIMARY_ENTITY_UUID,
    primary_entity_uuid: PRIMARY_ENTITY_UUID,
    total_linked_accounts: mockLinkedAccounts.length,
    linked_accounts: [...mockLinkedAccounts]
  }
}

function accountToLinked(account: AccountListItem, isPrimary: boolean, linkedAt: string): BffLinkedAccount {
  return {
    entity_uuid: account.entity_uuid,
    name: account.name,
    email: account.email,
    status: 'active',
    is_primary: isPrimary,
    linked_at: linkedAt
  }
}

// Mock account list (used by both staff /account/list/ and user /account/list/user/)
const mockAccountList: AccountListItem[] = [
  { entity_uuid: '550e8400-e29b-41d4-a716-446655440000', name: 'A55 Demo Company', email: 'demo@a55.tech', is_test_account: true },
  { entity_uuid: '660e8400-e29b-41d4-a716-446655440001', name: 'PAYSECURE LLC', email: 'contact@paysecure.com', is_test_account: false },
  { entity_uuid: '770e8400-e29b-41d4-a716-446655440002', name: '3STARS PAY LTDA', email: 'admin@3starspay.com.br', is_test_account: false },
  { entity_uuid: '880e8400-e29b-41d4-a716-446655440003', name: 'PAGSMILE', email: 'support@pagsmile.com', is_test_account: false },
  { entity_uuid: '990e8400-e29b-41d4-a716-446655440004', name: 'HANDMUSE', email: 'hello@handmuse.io', is_test_account: false },
  { entity_uuid: 'aa0e8400-e29b-41d4-a716-446655440005', name: 'GlamForce', email: 'info@glamforce.com', is_test_account: true },
  { entity_uuid: 'bb0e8400-e29b-41d4-a716-446655440006', name: 'Iunicharge', email: 'team@iunicharge.com', is_test_account: false },
  { entity_uuid: 'cc0e8400-e29b-41d4-a716-446655440007', name: 'CIEclothesfondyouw', email: 'contact@cieclothes.com', is_test_account: false },
  { entity_uuid: 'dd0e8400-e29b-41d4-a716-446655440008', name: 'Acme Payments', email: 'sales@acmepay.com', is_test_account: false },
  { entity_uuid: 'ee0e8400-e29b-41d4-a716-446655440009', name: 'Sandbox Co', email: 'dev@sandbox.io', is_test_account: true }
]

// Mock providers data
const mockProviders: BffProvider[] = [
  {
    internal_uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    name: 'Cielo',
    currency: 'BRL',
    sequence: 1,
    credit_card: true,
    debit_card: true,
    pix: true,
    boleto: true,
    e_wallet: false,
    spei: false,
    qra: false,
    pfc: false,
    apple_pay: true,
    google_pay: true,
    has_threeds: true,
    has_tokenization: true,
    has_refund: true
  },
  {
    internal_uuid: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
    name: 'MercadoPago',
    currency: 'MXN',
    sequence: 2,
    credit_card: true,
    debit_card: false,
    pix: false,
    boleto: false,
    e_wallet: true,
    spei: true,
    qra: false,
    pfc: false,
    apple_pay: false,
    google_pay: false,
    has_threeds: false,
    has_tokenization: false,
    has_refund: true
  },
  {
    internal_uuid: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    name: 'Pagarme',
    currency: 'BRL',
    sequence: 3,
    credit_card: true,
    debit_card: true,
    pix: true,
    boleto: true,
    e_wallet: false,
    spei: false,
    qra: false,
    pfc: false,
    apple_pay: false,
    google_pay: false,
    has_threeds: true,
    has_tokenization: true,
    has_refund: true
  }
]

export const accountHandlers = [
  /**
   * GET /api/bff/account/list/
   * Staff-only endpoint - returns ALL merchant accounts
   */
  http.get('/api/bff/account/list/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/list/')
    if (errorResponse) return errorResponse

    await applyDelay({ searchParams, preset: 'normal' })

    return HttpResponse.json(mockAccountList)
  }),

  /**
   * GET /api/bff/account/list/user/
   * Regular user endpoint - returns only accounts the user has access to (subset)
   */
  http.get('/api/bff/account/list/user/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/list/user/')
    if (errorResponse) return errorResponse

    await applyDelay({ searchParams, preset: 'normal' })

    return HttpResponse.json(mockAccountList.slice(0, 3))
  }),

  /**
   * GET /api/bff/account
   * Get BFF account details with providers and credentials
   */
  http.get('/api/bff/account', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data for counts
    const { merchants, wallets } = getMockData()

    const response: BffAccountResponse = {
      id: 123,
      entity_uuid: '550e8400-e29b-41d4-a716-446655440000',
      name: 'A55 Demo Company',
      email: 'demo@a55.tech',
      tax_id: '12.345.678/0001-90',
      status: 'active',
      is_test_account: true,
      merchants_count: merchants.length,
      wallets_count: wallets.length,
      providers_count: mockProviders.length,
      providers: mockProviders,
      created_at: '2024-01-15'
    }

    return HttpResponse.json(response)
  }),

  /**
   * GET /api/bff/account/
   * Get account details with statistics (legacy format)
   */
  http.get('/api/bff/account/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ searchParams, preset: 'normal' })

    // Get mock data
    const { merchants, wallets, transactions, settlements } = getMockData()

    // Calculate statistics from actual data
    const activeMerchants = merchants.filter(m => m.status === 'active')
    const activeWallets = wallets.filter(w => w.status === 'active')
    const confirmedTransactions = transactions.filter(t => t.status === 'confirmed')
    const pendingSettlements = settlements.filter(s => s.status === 'pending')
    const totalVolume = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)
    const totalFees = settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)

    const stats = calculateAccountStatistics({
      merchantCount: merchants.length,
      activeMerchantCount: activeMerchants.length,
      walletCount: wallets.length,
      activeWalletCount: activeWallets.length,
      transactionCount: transactions.length,
      totalVolume: Number(totalVolume.toFixed(2)),
      successRate: transactions.length > 0
        ? Number((confirmedTransactions.length / transactions.length).toFixed(4))
        : 0,
      settlementCount: settlements.length,
      pendingSettlementCount: pendingSettlements.length,
      totalFees: Number(totalFees.toFixed(2))
    })

    // Generate account with current statistics
    const account = generateAccount(stats)

    return HttpResponse.json({
      account,
      statistics: stats,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/account/usage/
   * Get current usage metrics
   */
  http.get('/api/bff/account/usage/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/usage/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data for calculating usage
    const { merchants, wallets, transactions, settlements } = getMockData()

    const confirmedTransactions = transactions.filter(t => t.status === 'confirmed')
    const totalVolume = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)

    // Calculate statistics
    const stats = calculateAccountStatistics({
      merchantCount: merchants.length,
      activeMerchantCount: merchants.filter(m => m.status === 'active').length,
      walletCount: wallets.length,
      activeWalletCount: wallets.filter(w => w.status === 'active').length,
      transactionCount: transactions.length,
      totalVolume: Number(totalVolume.toFixed(2)),
      successRate: transactions.length > 0
        ? Number((confirmedTransactions.length / transactions.length).toFixed(4))
        : 0,
      settlementCount: settlements.length,
      pendingSettlementCount: settlements.filter(s => s.status === 'pending').length,
      totalFees: settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)
    })

    // Generate account to get usage
    const account = generateAccount(stats)

    return HttpResponse.json({
      usage: account.usage,
      limits: account.limits,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/account/credentials/
   * Get current API credentials state
   */
  http.get('/api/bff/account/credentials/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/credentials/')
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'fast' })

    const response: BffCredentialsResponse = {
      entity_uuid: '550e8400-e29b-41d4-a716-446655440000',
      ...mockCredentialsState
    }

    return HttpResponse.json(response)
  }),

  /**
   * POST /api/bff/account/credentials/
   * Generate new API credentials
   */
  http.post('/api/bff/account/credentials/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/credentials/')
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'slow' })

    faker.seed(Date.now())
    const clientId = `client_${faker.string.alphanumeric(16)}`
    const clientSecret = `secret_${faker.string.alphanumeric(32)}`
    const createdAt = new Date().toISOString()

    mockCredentialsState = {
      has_credentials: true,
      client_id: clientId,
      client_secret: clientSecret,
      created_at: createdAt
    }

    const primaryAccount = mockAccountList.find(a => a.entity_uuid === PRIMARY_ENTITY_UUID)!
    mockLinkedAccounts = [
      accountToLinked(primaryAccount, true, createdAt)
    ]

    const response: BffCredentialsCreateResponse = {
      message: 'Credentials created successfully',
      entity_uuid: '550e8400-e29b-41d4-a716-446655440000',
      credentials_created: true,
      client_id: clientId,
      client_secret: clientSecret,
      created_at: createdAt
    }

    return HttpResponse.json(response)
  }),

  /**
   * DELETE /api/bff/account/credentials/
   * Revoke existing API credentials
   */
  http.delete('/api/bff/account/credentials/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/credentials/')
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'slow' })

    const affectedAccounts = mockLinkedAccounts.map(a => a.entity_uuid)
    const deletedClientId = mockCredentialsState.client_id

    mockCredentialsState = {
      has_credentials: false,
      client_id: null,
      client_secret: null,
      created_at: null
    }
    mockLinkedAccounts = []

    const response: BffCredentialsDeleteResponse = {
      message: 'Credentials deleted successfully',
      entity_uuid: PRIMARY_ENTITY_UUID,
      client_id: deletedClientId!,
      deleted: true,
      affected_accounts: affectedAccounts.length ? affectedAccounts : [PRIMARY_ENTITY_UUID]
    }

    return HttpResponse.json(response)
  }),

  /**
   * GET /api/bff/account/credentials/links/
   * List accounts sharing the same client_id
   */
  http.get('/api/bff/account/credentials/links/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/credentials/links/')
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'fast' })

    if (!mockCredentialsState.has_credentials) {
      return HttpResponse.json(
        { message: 'No credentials found for this account', code: 'credentials_not_found' },
        { status: 400 }
      )
    }

    return HttpResponse.json(buildLinksResponse())
  }),

  /**
   * POST /api/bff/account/credentials/links/
   * Link additional merchant accounts to the same client_id
   */
  http.post('/api/bff/account/credentials/links/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/credentials/links/')
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'slow' })

    if (!mockCredentialsState.has_credentials) {
      return HttpResponse.json(
        { message: 'No credentials found for this account', code: 'credentials_not_found' },
        { status: 400 }
      )
    }

    const body = await request.json() as { entity_uuids?: string[], entityUuids?: string[] }
    const entityUuids = body.entity_uuids ?? body.entityUuids ?? []

    const linkedUuids: string[] = []
    const skippedUuids: string[] = []
    const linkedUuidSet = new Set(mockLinkedAccounts.map(a => a.entity_uuid))
    const merchantUuids = new Set(mockAccountList.map(a => a.entity_uuid))

    for (const uuid of entityUuids) {
      if (!merchantUuids.has(uuid)) {
        return HttpResponse.json(
          { message: `Account ${uuid} is not accessible for this merchant`, code: 'account_access_denied' },
          { status: 403 }
        )
      }

      if (accountsWithOtherCredentials.has(uuid)) {
        return HttpResponse.json(
          { message: `Account ${uuid} already has credentials from another client`, code: 'credentials_conflict' },
          { status: 409 }
        )
      }

      if (linkedUuidSet.has(uuid)) {
        skippedUuids.push(uuid)
        continue
      }

      const account = mockAccountList.find(a => a.entity_uuid === uuid)!
      mockLinkedAccounts.push(accountToLinked(account, false, new Date().toISOString()))
      linkedUuidSet.add(uuid)
      linkedUuids.push(uuid)
    }

    const response: BffCredentialsLinkCreateResponse = {
      message: 'Credential links updated successfully',
      source_entity_uuid: PRIMARY_ENTITY_UUID,
      client_id: mockCredentialsState.client_id!,
      linked_entity_uuids: linkedUuids,
      skipped_entity_uuids: skippedUuids,
      total_linked_accounts: mockLinkedAccounts.length
    }

    return HttpResponse.json(response, { status: 201 })
  }),

  /**
   * DELETE /api/bff/account/credentials/links/:target/
   * Remove a linked account from credential sharing
   */
  http.delete('/api/bff/account/credentials/links/:target/', async ({ request, params }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams
    const target = params.target as string

    const errorResponse = handleErrorSimulation(searchParams, {}, `/api/bff/account/credentials/links/${target}/`)
    if (errorResponse) return errorResponse

    await applyDelay({ preset: 'fast' })

    if (!mockCredentialsState.has_credentials) {
      return HttpResponse.json(
        { message: 'No credentials found for this account', code: 'credentials_not_found' },
        { status: 400 }
      )
    }

    const index = mockLinkedAccounts.findIndex(a => a.entity_uuid === target)
    if (index === -1) {
      return HttpResponse.json(
        { message: 'Credential link not found', code: 'credential_link_not_found' },
        { status: 400 }
      )
    }

    if (mockLinkedAccounts[index]!.is_primary) {
      return HttpResponse.json(
        { message: 'Cannot unlink the primary credentials account', code: 'credential_link_not_found' },
        { status: 400 }
      )
    }

    mockLinkedAccounts.splice(index, 1)

    const response: BffCredentialsLinkDeleteResponse = {
      message: 'Credential link removed successfully',
      source_entity_uuid: PRIMARY_ENTITY_UUID,
      target_entity_uuid: target,
      client_id: mockCredentialsState.client_id!,
      removed_from_account: true,
      cognito_deleted: false,
      remaining_linked_accounts: mockLinkedAccounts.length
    }

    return HttpResponse.json(response)
  }),

  /**
   * GET /api/bff/account/api-keys/
   * Get API keys list
   */
  http.get('/api/bff/account/api-keys/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/api-keys/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data for calculating stats
    const { merchants, wallets, transactions, settlements } = getMockData()

    const confirmedTransactions = transactions.filter(t => t.status === 'confirmed')
    const totalVolume = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)

    const stats = calculateAccountStatistics({
      merchantCount: merchants.length,
      activeMerchantCount: merchants.filter(m => m.status === 'active').length,
      walletCount: wallets.length,
      activeWalletCount: wallets.filter(w => w.status === 'active').length,
      transactionCount: transactions.length,
      totalVolume: Number(totalVolume.toFixed(2)),
      successRate: transactions.length > 0
        ? Number((confirmedTransactions.length / transactions.length).toFixed(4))
        : 0,
      settlementCount: settlements.length,
      pendingSettlementCount: settlements.filter(s => s.status === 'pending').length,
      totalFees: settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)
    })

    const account = generateAccount(stats)

    return HttpResponse.json({
      apiKeys: account.apiKeys,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/account/team/
   * Get team members list
   */
  http.get('/api/bff/account/team/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/team/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data for calculating stats
    const { merchants, wallets, transactions, settlements } = getMockData()

    const confirmedTransactions = transactions.filter(t => t.status === 'confirmed')
    const totalVolume = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)

    const stats = calculateAccountStatistics({
      merchantCount: merchants.length,
      activeMerchantCount: merchants.filter(m => m.status === 'active').length,
      walletCount: wallets.length,
      activeWalletCount: wallets.filter(w => w.status === 'active').length,
      transactionCount: transactions.length,
      totalVolume: Number(totalVolume.toFixed(2)),
      successRate: transactions.length > 0
        ? Number((confirmedTransactions.length / transactions.length).toFixed(4))
        : 0,
      settlementCount: settlements.length,
      pendingSettlementCount: settlements.filter(s => s.status === 'pending').length,
      totalFees: settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)
    })

    const account = generateAccount(stats)

    return HttpResponse.json({
      teamMembers: account.teamMembers,
      limits: {
        maxUsers: account.limits.maxUsersPerAccount,
        currentUsers: account.teamMembers.filter(m => m.status === 'active').length
      },
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  }),

  /**
   * GET /api/bff/account/integrations/
   * Get integrations list
   */
  http.get('/api/bff/account/integrations/', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Handle error simulation
    const errorResponse = handleErrorSimulation(searchParams, {}, '/api/bff/account/integrations/')
    if (errorResponse) return errorResponse

    // Apply delay
    await applyDelay({ preset: 'fast' })

    // Get mock data for calculating stats
    const { merchants, wallets, transactions, settlements } = getMockData()

    const confirmedTransactions = transactions.filter(t => t.status === 'confirmed')
    const totalVolume = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)

    const stats = calculateAccountStatistics({
      merchantCount: merchants.length,
      activeMerchantCount: merchants.filter(m => m.status === 'active').length,
      walletCount: wallets.length,
      activeWalletCount: wallets.filter(w => w.status === 'active').length,
      transactionCount: transactions.length,
      totalVolume: Number(totalVolume.toFixed(2)),
      successRate: transactions.length > 0
        ? Number((confirmedTransactions.length / transactions.length).toFixed(4))
        : 0,
      settlementCount: settlements.length,
      pendingSettlementCount: settlements.filter(s => s.status === 'pending').length,
      totalFees: settlements.reduce((sum, s) => sum + s.fees.totalFees, 0)
    })

    const account = generateAccount(stats)

    return HttpResponse.json({
      integrations: account.integrations,
      meta: {
        requestTime: new Date().toISOString(),
        processingTimeMs: 0
      }
    })
  })
]
